import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

// Super admin → "View as Community Manager": choose which of the portfolio's communities the
// preview seat is assigned to. The list is fetched WITHOUT the preview headers so it's complete.
export function PortfolioManagerPicker({ open, onOpenChange, company, initial = [], onChoose }) {
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState(new Set(initial));
  useEffect(() => {
    if (!open) return;
    setPicked(new Set(initial));
    setRows(null);
    api.get("/tenants", { headers: { "X-Preview-As": "" } })
      .then((r) => setRows((r.data || []).filter((t) => !company?.id || t.company_id === company.id).sort((a, b) => (a.name || "").localeCompare(b.name || ""))))
      .catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company?.id]);
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="portfolio-manager-picker">
        <DialogHeader><DialogTitle className="font-heading">View as a Community Manager</DialogTitle></DialogHeader>
        <p className="text-sm text-[#57534E] -mt-1">A Community Manager runs a set of communities at {company?.name || "the portfolio"}. Pick the communities this one is assigned to — everything you see next is exactly what they'd see.</p>
        {!rows ? (
          <div className="flex items-center gap-2 text-sm text-[#8C827A] py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading communities…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-[#8C827A] py-4" data-testid="portfolio-manager-picker-empty">No communities in this portfolio yet.</div>
        ) : (
          <div className="max-h-72 overflow-y-auto rounded-xl border border-sand-200 divide-y divide-sand-100">
            {rows.map((t) => (
              <label key={t.id} className="flex items-center gap-3 px-3.5 py-2.5 text-sm cursor-pointer hover:bg-sand-50" data-testid={`portfolio-manager-pick-${t.id}`}>
                <Checkbox checked={picked.has(t.id)} onCheckedChange={() => toggle(t.id)} />
                <span className="flex-1 truncate font-semibold text-[#1C1917]">{t.name}</span>
                {t.unit_count ? <span className="text-xs text-[#8C827A]">{t.unit_count} homes</span> : null}
              </label>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-xs text-[#8C827A]">{picked.size} of {rows?.length ?? "…"} selected</span>
          <span className="flex gap-2">
            <button type="button" data-testid="portfolio-manager-cancel" onClick={() => onOpenChange(false)} className="rounded-full px-4 py-2 text-sm font-semibold text-[#57534E] hover:bg-sand-100">Cancel</button>
            <button type="button" data-testid="portfolio-manager-apply" disabled={picked.size === 0} onClick={() => onChoose([...picked])}
              className="rounded-full bg-ink text-white px-4 py-2 text-sm font-bold hover:bg-ink-700 disabled:opacity-50">View as Community Manager</button>
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
